import React, { useMemo } from 'react';

import { Avatar, CardHeader, Spinner, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useProcessedImageUrl } from 'src/app/shared/hooks/image';

import { isFieldFocused, shouldHighlightText } from '../../helpers/validation';
import type { FocusedField } from '../../types';
import { ActionsMenu } from '../ActionsMenu';
import { LinkContent } from '../LinkContent';

type EndorsementCardHeaderProps = {
	displayName: string;
	displayTitle: string;
	photoUrl: string;
	link?: string;
	isNamePlaceholder: boolean;
	isTitlePlaceholder: boolean;
	focusedField: FocusedField;
	onEdit?: () => void;
	onRemove?: () => void;
};

export const EndorsementCardHeader: React.FC<EndorsementCardHeaderProps> = ({
	displayName,
	displayTitle,
	photoUrl,
	link,
	isNamePlaceholder,
	isTitlePlaceholder,
	focusedField,
	onEdit,
	onRemove,
}) => {
	const image = useMemo(() => {
		if (!photoUrl) return undefined;
		const processing = photoUrl.includes('#processing');
		return { url: photoUrl, processing };
	}, [photoUrl]);

	const resolvedPhotoUrl = useProcessedImageUrl(image);
	const isImageProcessing = !!image && !resolvedPhotoUrl;

	const isNameFocused = isFieldFocused('name', focusedField) || isFieldFocused('url', focusedField);
	const isTitleFocused = isFieldFocused('title', focusedField);
	const shouldHighlightTitle = shouldHighlightText(isTitleFocused, !isTitlePlaceholder);

	return (
		<CardHeader className="flex items-center justify-between p-0!">
			<div className="flex items-center gap-4">
				{isImageProcessing ? (
					<div
						className="bg-background-secondary flex size-14 shrink-0 items-center justify-center rounded-full"
						data-testid="endorsement-photo-processing"
						aria-hidden="true"
					>
						<Spinner className="size-4" />
					</div>
				) : (
					<Avatar size="large" alt={displayName} src={resolvedPhotoUrl || undefined} />
				)}
				<div className="flex flex-col justify-between">
					<LinkContent name={displayName} link={link} isPlaceholder={isNamePlaceholder} hasFocus={isNameFocused} />
					<Text
						variant="body"
						size="small"
						fontWeight="normal"
						className={cn('text-typography-strong', shouldHighlightTitle && 'text-typography-interactive!')}
					>
						{displayTitle}
					</Text>
				</div>
			</div>
			{(onEdit || onRemove) && <ActionsMenu onEdit={onEdit} onRemove={onRemove} />}
		</CardHeader>
	);
};
